import { useAsyncExecutor, useAsyncExecutorPool } from "async-executor-hook";
import { Toast } from "primereact/toast";
import { FC, useEffect, useReducer, useRef, useState } from "react";
import {
  FormControl,
  FormGroup,
  useFormConfig,
  Validators,
  WForm,
  WFormControl,
} from "rectangular-forms";
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  from,
  map,
  mergeMap,
  of,
  tap,
} from "rxjs";

import { Authorization } from "../../common/AuthorizationCpt";
import {
  inputMaxLengthFn,
  inputUperCaseFn,
  onlyDigits,
  WDropdown,
  WInputText,
  WMultiSelect,
} from "../../common/components/TemplateContainer/InputTemplates";
import { SaveButton } from "../../common/components/TemplateContainer/SaveButton";
import { TemplateInputContainer } from "../../common/components/TemplateContainer/TemplateContainer";
import { getErrorValueResponse } from "../../common/error/Error";
import IconVectorRightBlue from "../../images/icon_button_users_blue.svg";
import { FILE_FORMATS, HTTP_METHODS } from "../../utils/constants";
import { networkBackendRequest } from "../../utils/networkUtils";
import { uploadFileToS3 } from "../../utils/s3Upload";
import {
  DOCUMENT_DNI_MAX_LENGTH,
  DOCUMENT_NUMBER_MAX_LENGTH,
  DOCUMENT_TYPE_MAX_LENGTH,
  EMAIL_MAX_LENGTH,
  FULL_NAME_MAX_LENGTH,
  JOB_MAX_LENGTH,
  PASSPORT_NUMBER_MAX_LENGTH,
  PASSPORT_NUMBER_MIN_LENGTH,
  PASSWORD_CREATE_USER,
  PHONE_NUMBER_MAX_LENGTH,
  TYPE_PASSPORT_NUMBER_MIN_LENGTH,
} from "../../utils/valueStaticConstants";
import FileUploadInputDirect from "./FileUploadInputDirect";
import HoverMessage from "./HoverMessage";
import {
  DivDocumentUpload,
  DivFlex,
  DivOverMaxHeigth,
} from "./UsersManagementAdminStyles";

interface IUsersManagementStepTwoAdminForm {
  toggleModal: () => void;
  dataInitialUsers: any;
  dataInitialCompany: {};
  dataUserEditAdmin: any;
  loadRucSuccess: {};
  setShowModalSuccessRegister: any;
  setMessageSuccessRegister: any;
  setTitleMessageUsers: any;
  setDataInitialCompany: any;
  setDataInitialUsers: any;
}
const UsersManagementStepTwoAdminForm: FC<IUsersManagementStepTwoAdminForm> = (
  props
) => {
  const {
    toggleModal,
    dataInitialUsers,
    dataInitialCompany,
    dataUserEditAdmin,
    loadRucSuccess,
    setShowModalSuccessRegister,
    setMessageSuccessRegister,
    setTitleMessageUsers,
    setDataInitialCompany,
    setDataInitialUsers,
  } = props;
  const [selectedFiles, setSelectedFiles] = useState([]);
  const onFileChange = (files) => {
    formConfig.form.get("files").setValue(files);
    setSelectedFiles(files);
  };
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const operationNumberValidation =
    (forceUpdate: () => void) => (formControl: FormControl) => {
      const subject: any = new BehaviorSubject("");
      subject.next(formControl.value);
      const debouncerTime$ = subject.asObservable().pipe(
        debounceTime(1000),
        mergeMap((email: string) => {
          return from(
            networkBackendRequest(
              "/users/check-email/",
              HTTP_METHODS.GET,
              { email },
              undefined
            ).then((resp) => resp.json())
          ).pipe(
            map((isAvailable: boolean) => {
              return !isAvailable
                ? {
                    emailIsTaken: true,
                  }
                : null;
            }),
            catchError(() => {
              return of({ emailIsTaken: true });
            }),
            tap((x) => {
              setTimeout(() => {
                forceUpdate();
              }, 40);
            })
          );
        })
      );
      return debouncerTime$;
    };

  const toast: any = useRef();
  const formConfig = useFormConfig({
    createForm: () => {
      const form = new FormGroup({
        full_name: new FormControl(null, [
          Validators.required,
          Validators.maxLength(FULL_NAME_MAX_LENGTH),
          Validators.pattern(/^[A-Z \-ÁÉÍÓÚÑ]*$/),
        ]),
        email: new FormControl(
          null,
          [
            Validators.required,
            Validators.pattern(/^(.+)@(.+)\.(.+)$/),
            Validators.maxLength(EMAIL_MAX_LENGTH),
          ],
          operationNumberValidation(forceUpdate)
        ),
        document_type: new FormControl(null, [Validators.required]),
        document_number: new FormControl(null, [
          Validators.required,
          Validators.maxLength(DOCUMENT_NUMBER_MAX_LENGTH),
        ]),
        phone_number: new FormControl(null, [
          Validators.required,
          Validators.pattern(/^[0-9]{7,15}$/),
          Validators.maxLength(PHONE_NUMBER_MAX_LENGTH),
        ]),
        job: new FormControl(null, [
          Validators.required,
          Validators.maxLength(JOB_MAX_LENGTH),
        ]),
        authorization_doc: new FormControl(null, []),
        role: new FormControl(null, [Validators.required]),
        profile_id: new FormControl(null, []),
        files: new FormControl(null, []),
        processes: new FormControl([], [Validators.required]),
      });
      if (dataUserEditAdmin) {
        form.get("role").disable();
        form.get("email").disable();
      }
      form
        .get("document_type")
        .valueChanges.pipe(
          tap((document_type) => {
            const document_numberFC = form.get("document_number");
            document_numberFC.clearValidators();
            if (document_type === "dni") {
              document_numberFC.setValidators([
                Validators.required,
                Validators.minLength(DOCUMENT_DNI_MAX_LENGTH),
                Validators.maxLength(DOCUMENT_DNI_MAX_LENGTH),
              ]);
              setSocumentNumberMaxLength(DOCUMENT_DNI_MAX_LENGTH);
            } else if (document_type === "passport") {
              document_numberFC.setValidators([
                Validators.required,
                Validators.minLength(TYPE_PASSPORT_NUMBER_MIN_LENGTH),
                Validators.maxLength(PASSPORT_NUMBER_MAX_LENGTH),
              ]);
              setSocumentNumberMaxLength(PASSPORT_NUMBER_MAX_LENGTH);
            } else {
              document_numberFC.setValidators([
                Validators.required,
                Validators.minLength(PASSPORT_NUMBER_MIN_LENGTH),
                Validators.maxLength(DOCUMENT_TYPE_MAX_LENGTH),
              ]);
              setSocumentNumberMaxLength(DOCUMENT_TYPE_MAX_LENGTH);
            }
            document_numberFC.updateValueAndValidity();
          })
        )
        .subscribe();
      return form;
    },
    onFormLoaded: (form, data) => {
      form.patchValue({ ...data, processes: data.processes || [] });
    },
    onSubmit: (form, { processing, succeed, failed }) => {
      if (form.invalid) {
        toast.current.show({
          severity: "error",
          summary:
            "Completar todos los campos obligatorios antes de continuar. ¡Gracias!",
          contentClassName: "",
        });
        return;
      }
      processing();
      createUser.execute({
        data: form.value,
        succeed,
        failed,
        loadRucSuccess,
        dataInitialCompany,
        selectedFiles,
        dataUserEditAdmin,
      });
    },
  });

  const { loadSucceed } = formConfig;

  useEffect(() => {
    const data = {
      ...(dataUserEditAdmin || dataInitialUsers),
      role: dataInitialUsers && dataInitialUsers.role.id,
      profile_id: dataUserEditAdmin && dataUserEditAdmin.role.profile.id,
      processes:
        dataUserEditAdmin &&
        dataUserEditAdmin.processes.map((process) => {
          return process.id;
        }),
    };
    loadSucceed(data);
  }, [loadSucceed, dataUserEditAdmin, dataInitialUsers]);

  const createUser = useAsyncExecutor(
    async (params: any) => {
      const { data, dataInitialCompany, loadRucSuccess, dataUserEditAdmin } =
        params;
      const idUserEdit = dataUserEditAdmin.id;
      const company = {
        ...dataInitialCompany,
        ruc: dataUserEditAdmin
          ? dataUserEditAdmin.company.ruc
          : loadRucSuccess.ruc,
      };

      const bodyEdit = {
        processes: data.processes && data.processes,
        user: {
          document_number: data.document_number,
          document_type: data.document_type,
          full_name: data.full_name,
          job: data.job,
          phone_number: data.phone_number,
        },
        company: company,
        profile_id: data.profile_id,
      };
      let myFile: any = "";
      if (data.files) {
        myFile = await uploadFileToS3(
          "user-authorization",
          data.files,
          "user-authorization-docs"
        );
      }

      const bodyUsers = {
        user: {
          full_name: data.full_name,
          email: data.email,
          document_type: data.document_type,
          document_number: data.document_number,
          phone_number: data.phone_number,
          authorization_doc: myFile[0] || data.authorization_doc,
          job: data.job,
        },
      };
      const myBody = {
        user: bodyUsers.user,
        profile_id: data.profile_id,
        company: company,
        processes: data.processes && data.processes,
      };
      return networkBackendRequest(
        idUserEdit ? `/users/${idUserEdit}/` : `/users/`,
        idUserEdit ? HTTP_METHODS.PATCH : HTTP_METHODS.POST,
        {},
        idUserEdit ? bodyEdit : myBody
      ).then((resp) => resp.json());
    },
    {
      onSuccess: (resp, params: any) => {
        params.succeed();
        if (params.dataUserEditAdmin) {
          setTitleMessageUsers(`Usuario actualizado exitosamente`);
          setMessageSuccessRegister(
            `El usuario ${resp.full_name} ha sido actualizado exitosamente`
          );
          setShowModalSuccessRegister(true);
        } else {
          setTitleMessageUsers(`Usuario creado exitosamente`);
          setMessageSuccessRegister(
            `Se ha registrado el usuario ${resp.full_name}. Recordar que la contraseña temporal es ${PASSWORD_CREATE_USER}`
          );
          setShowModalSuccessRegister(true);
        }
        toggleModal();
        setDataInitialCompany({});
        setDataInitialUsers({});
      },
      onError: (error, params: any) => {
        params.failed();
        const errorMessage = getErrorValueResponse(error);
        toast.current.show({
          severity: "error",
          summary: "Atención",
          detail: errorMessage,
          contentClassName: "",
          life: 5000,
        });
      },
    }
  );

  const asyncExecutorPoolRolBackoffice = useAsyncExecutorPool("");
  const loadRolesProfilesBackoffice: any =
    asyncExecutorPoolRolBackoffice.getExecutor("loadRolProfilesUserBackoffice");
  const asyncExecutorPoolRolProcessesBackoffice = useAsyncExecutorPool("");
  const loadRolProcessesBackoffice: any =
    asyncExecutorPoolRolProcessesBackoffice.getExecutor(
      "loadRolProcessesUserBackoffice"
    );
  useEffect(() => {
    if (!loadRolesProfilesBackoffice || !loadRolProcessesBackoffice) return;
  }, [
    loadRolesProfilesBackoffice,
    asyncExecutorPoolRolBackoffice,
    asyncExecutorPoolRolProcessesBackoffice,
    loadRolProcessesBackoffice,
  ]);
  const valueLabelProcesses = loadRolProcessesBackoffice?.data?.processes.map(
    (process) => {
      return {
        label: `${process.name}`,
        value: `${process.id}`,
      };
    }
  );
  const selectRol = loadRolesProfilesBackoffice?.data?.map((rolselect) => {
    return {
      label: rolselect.name,
      value: rolselect.id,
    };
  });
  const [idValueChange, setIdValueChange] = useState();
  const findUserProfileId = loadRolesProfilesBackoffice?.data?.find(
    (profile) => {
      return profile.id === idValueChange;
    }
  );

  const dataRolAdmin =
    findUserProfileId &&
    findUserProfileId.profiles.map((profile) => {
      return {
        label: `${profile.name} de ${findUserProfileId.name}`,
        value: profile.id,
      };
    });
  const filterRolAdmin =
    dataRolAdmin &&
    dataRolAdmin.filter((rol) => {
      return rol.label !== "Administrador de BackOffice";
    });

  const [idValueRol, setIdValueRol] = useState();
  const showDocument =
    filterRolAdmin &&
    filterRolAdmin.filter((admin) => {
      return (
        admin.value === idValueRol &&
        admin.label === "Administrador de Agente de Aduana"
      );
    });

  const [documentNumberMaxLength, setSocumentNumberMaxLength] = useState(
    DOCUMENT_NUMBER_MAX_LENGTH
  );
  return (
    <DivOverMaxHeigth>
      <WForm
        formConfig={formConfig}
        globalErrorMessages={{
          minlength: (val) => {
            return `El valor tiene ${val.actualLength}  dígitos. Pero debe tener un mínimo de ${val.requiredLength} dígitos`;
          },
          maxlength: (val) => {
            return `Máximo Valor ${val.requiredLength}`;
          },
          required: "Campo requerido",
          emailIsTaken: "Ya existe una cuenta creada con este correo",
        }}
      >
        <Toast ref={toast}></Toast>
        <div className="p-grid" style={{ display: "flex" }}>
          <div className="p-md-2" style={{ width: "53.3rem" }}>
            <WFormControl
              name="full_name"
              errorMessages={{ pattern: "Solo mayúsculas" }}
            >
              <TemplateInputContainer label="Nombres y apellidos:">
                <WInputText
                  width="53.3rem"
                  height="5rem"
                  fontSize="1.4rem"
                  placeholder="NOMBRES Y APELLIDOS"
                  maxLength={FULL_NAME_MAX_LENGTH}
                  onInput={inputUperCaseFn}
                />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div
            className="p-md-2"
            style={{ width: "25.6rem", marginLeft: "2rem" }}
          >
            <WFormControl
              name="email"
              errorMessages={{ pattern: "Email inválido" }}
            >
              <TemplateInputContainer label="Correo electrónico:">
                <WInputText
                  width="25.6rem"
                  placeholder="Correo electrónico"
                  maxLength={EMAIL_MAX_LENGTH}
                  onInput={inputMaxLengthFn}
                />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div
            className="p-md-2"
            style={{ width: "25.6rem", marginLeft: "2rem" }}
          >
            <WFormControl name="job">
              <TemplateInputContainer label="Cargo:">
                <WInputText
                  width="25.6rem"
                  placeholder="Escriba el cargo"
                  maxLength={JOB_MAX_LENGTH}
                  onInput={inputMaxLengthFn}
                />
              </TemplateInputContainer>
            </WFormControl>
          </div>
        </div>
        <div
          className="p-grid"
          style={{ display: "flex", marginTop: "3.2rem" }}
        >
          <div className="p-md-2" style={{ width: "25.6rem" }}>
            <WFormControl name="document_type">
              <TemplateInputContainer label="Tipo de documento:">
                <WDropdown
                  options={[
                    { label: "DNI", value: "dni" },
                    { label: "Pasaporte", value: "passport" },
                    {
                      label: "Carnet de extranjería",
                      value: "immigration_card",
                    },
                  ]}
                  width="25.6rem"
                  placeholder="(Seleccione el tipo de doc)"
                />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div
            className="p-md-2"
            style={{ width: "25.6rem", marginLeft: "2rem" }}
          >
            <WFormControl name="document_number">
              <TemplateInputContainer label="DNI/CE/Pasaporte:">
                <WInputText
                  width="25.6rem"
                  placeholder="DNI/CE/Pasaporte"
                  maxLength={documentNumberMaxLength}
                  onInput={inputMaxLengthFn}
                  onKeyPress={onlyDigits}
                />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div
            className="p-md-2"
            style={{ width: "25.6rem", marginLeft: "2rem" }}
          >
            <WFormControl
              name="phone_number"
              errorMessages={{ pattern: "Ingrese datos correctos" }}
            >
              <TemplateInputContainer label="Celular:">
                <WInputText
                  width={"25.6rem"}
                  placeholder="Celular"
                  maxLength={PHONE_NUMBER_MAX_LENGTH}
                  onInput={inputMaxLengthFn}
                  onKeyPress={onlyDigits}
                />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div
            className="p-md-2"
            style={{ width: "25.6rem", marginLeft: "2rem" }}
          >
            <WFormControl
              name="role"
              onChange={({ value }) => {
                setIdValueChange(value);
              }}
            >
              {dataUserEditAdmin && (
                <DivFlex width="24.6rem">
                  <HoverMessage leftBefore={"26.5rem"}>
                    Puedes eliminar este usuario y crearlo con el rol
                    correspondiente
                  </HoverMessage>
                </DivFlex>
              )}
              <TemplateInputContainer
                label="Rol:"
                loadStatus={loadRolesProfilesBackoffice?.status}
              >
                <WDropdown
                  options={selectRol}
                  width="25.6rem"
                  placeholder="(Seleccione los roles)"
                />
              </TemplateInputContainer>
            </WFormControl>
          </div>
        </div>
        <div
          className="p-grid"
          style={{ display: "flex", marginTop: "3.2rem" }}
        >
          <div className="p-md-2" style={{ width: "25.6rem" }}>
            <WFormControl
              name="profile_id"
              onChange={({ value }) => {
                setIdValueRol(value);
              }}
            >
              <TemplateInputContainer
                label="Perfil:"
                loadStatus={loadRolesProfilesBackoffice?.status}
              >
                <WDropdown
                  options={filterRolAdmin || []}
                  width={"25.6rem"}
                  placeholder="(Seleccione un perfil)"
                />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div
            className="p-md-2"
            style={{ width: "53.3rem", marginLeft: "2rem" }}
          >
            <WFormControl name="processes">
              <TemplateInputContainer
                label="Proceso:"
                loadStatus={loadRolProcessesBackoffice?.status}
              >
                <WMultiSelect
                  options={valueLabelProcesses || []}
                  width={"53.3rem"}
                  placeholder="(Seleccione uno o más procesos)"
                />
              </TemplateInputContainer>
            </WFormControl>
          </div>
          <div
            className="p-md-2"
            style={{ width: "25.6rem", marginLeft: "2rem" }}
          >
            {!dataUserEditAdmin && showDocument && showDocument.length > 0 && (
              <DivDocumentUpload>
                <div className="document-title">
                  <b>Adjuntar documento:</b>
                </div>
                <div className="margin-top1">
                  <FileUploadInputDirect
                    maxFiles={1}
                    onValueChange={onFileChange}
                    value={selectedFiles}
                    formats={[FILE_FORMATS.IMAGE, FILE_FORMATS.PDF]}
                    width={"25.6rem"}
                    height={"5rem"}
                  />
                </div>
                <div className="max-mb-document">Máximo 5MB</div>
              </DivDocumentUpload>
            )}
          </div>
        </div>
        <div style={{ marginTop: 20 }}>
          <Authorization actions={["user.update_users", "user.create_users"]}>
            <SaveButton
              label={
                dataUserEditAdmin ? "Guardar usuario" : "Crear nuevo usuario"
              }
              iconRight={IconVectorRightBlue}
              width="25.6rem"
              height="6rem"
              fontSize="1.5rem"
              backgroundColor="#88B13B"
            />
          </Authorization>
        </div>
      </WForm>
    </DivOverMaxHeigth>
  );
};

export default UsersManagementStepTwoAdminForm;
